import React, { useState, useRef, useEffect } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import theme from '../styles/theme';
import { FaCircleExclamation } from 'react-icons/fa6';
import { PoliceReportsService } from '../services/police-reports-service';
import { useAuth } from '../auth/AuthContext';
import type { CombinedReportType } from '../types/CombinedReport';

const Title = styled.h3`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 20px;
    font-family: ${props => props.theme.font.family};
    font-size: ${props => props.theme.font.size.large};
    font-style: ${props => props.theme.font.style};
    font-weight: ${props => props.theme.font.weight.regular};
`;
const Container = styled.div`  
    width: 80%;  
    margin: 0 auto;  
    font-family: ${props => props.theme.font.family};
    font-size: ${props => props.theme.font.size.regular};
    font-style: ${props => props.theme.font.style};
    font-weight: ${props => props.theme.font.weight.regular};
`;

const Text = styled.p`
    padding: 10px 20px;
    text-align: left;
    italics: true;
`;

const List = styled.ul`
`;

const ListItem = styled.li`
`;

const FormWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    padding: 10px 20px;
`;

const InputWrapper = styled.div`
    display:flex;
    flex-direction: row;
    justify-content: spaced-evenly;
    
    @media screen and (max-width: 630px) {
        flex-direction: column;
    }
`;

const Input = styled.input`
    resize: none;
    text-align: center;
    margin: 5px;
    display: inline-block;  
    outline: 0;
    background: #f2f2f2;
    width: 100%;
    border: 0;
    border-radius: .25rem;
    padding: 1rem;
    box-sizing: border-box;
    cursor: text;
`;

const Select = styled.select`
    resize: none;
    text-align: center;
    min-height: 3rem;
    width: auto;
    margin: 5px;
    display: inline-block;  
    outline: 0;
    background: #f2f2f2;
    border: none;
    border-radius: .25rem;
    cursor: pointer;
`;

const Button = styled.button<{ disabled: boolean }>`  
    border: none;
    border-radius: .25rem;
    padding: 15px 32px;  
    margin: .5rem;  
    text-align: center;  
    text-decoration: none;  
    display: inline-block;  
    font-size: 16px;  
    opacity: ${props => (props.disabled ? 0.5 : 1)};  
    pointer-events: ${props => (props.disabled ? 'none' : 'auto')};  
    cursor: pointer;  

    &:hover {
        background-color: #ddd;
    }
`;

const ExclamationIcon = styled(FaCircleExclamation)`
    height: 1rem;
    width: auto;
`;

const AccessMsg = styled.p`
    color: red;
`;

const SemiBold = styled.span`
    font-weight: ${props => props.theme.font.weight.SemiBold};
`;

const ReportSearch = () => {
    const [isMaxReports, setIsMaxReports] = useState(true);
    const [accessLimit, setAccessLimit] = useState(0);
    const [reportCount, setReportCount] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const yearRef = useRef<HTMLSelectElement>(null);
    const caseNumberRef = useRef<HTMLInputElement>(null);
    const dateRef = useRef<HTMLInputElement>(null);

    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();

    useEffect(() => {
        const fetchIsMaxReports = async () => {
            try {
                const reportCount = await PoliceReportsService.getReportCount();
                const accessLimit = await PoliceReportsService.getAccessLimit();
                setReportCount(reportCount);
                setAccessLimit(accessLimit);
                setIsMaxReports(reportCount < accessLimit);
            } catch (error) {
                console.error('Failed to fetch report data: ', error);
            }
        };

        fetchIsMaxReports();
    }, [isAuthenticated]);

    const handleSubmit = async () => {
        const year = yearRef.current?.value || '';
        const caseNumber = caseNumberRef.current?.value || '';
        const dateValue = dateRef.current?.value || '';
        const date = dateValue ? new Date(dateValue) : null;

        try {
            const caseNumberWithYear = `${year}${caseNumber}`;
            if (!caseNumberWithYear || !date) {
                setErrorMessage('Please provide both case number and date.');
                return;
            }
            const reportData: CombinedReportType = await PoliceReportsService.getCombinedReport(caseNumberWithYear, date);
            setErrorMessage(null); // Clear any previous error message
            if (!reportData) {
                setErrorMessage('Failed to fetch report data. Please make sure you have entered the case number and date correctly, and that you have not exceeded your access limit.');
            } else {
                if (typeof reportData === 'string') {
                    setErrorMessage(reportData);
                    return;
                }
                navigate('/Report', { state: { year, caseNumber, reportData } });
            }
        } catch (error) {
            console.error('Failed to fetch report data:', error);
            setErrorMessage('Failed to fetch report data. Please make sure you have entered the case number and date correctly, and that you have not exceeded your access limit.');
        }
    };

    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 1986; year--) {
        years.push(year.toString().slice(2));
    }

    const invalidKeyCodes = new Set([
        ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(i + 65)), // A-Z
        ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(i + 97)), // a-z
        '`', '~', '!', '@', '#', '$', '%', '^', '&', '*', '-', '_', '=', '+',  // symbols
        '(', ')', '{', '}', '[', ']', '{', '}', // symbols
        '\\', '|', ';', ':', "'", '"', ',', '<', '.', '>', '/', '?', // symbols
    ]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const { key, shiftKey } = event;
        const isShiftNumber = shiftKey && key >= '0' && key <= '9';
        if (invalidKeyCodes.has(key) || isShiftNumber) {
            event.preventDefault();
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Container>
                <Title>Lookup Online Police Reports</Title>
                <Text>Enter case number and press Submit to get a public-copy police report</Text>
                <List>
                    <ListItem>You must select the first two digits of the police report number - which represent the year the report was filed - from the drop-down list.&nbsp; Then enter the remaining digits of the police report number and press the 'Submit' button.</ListItem>
                    <ListItem>Please note that if the remaining digits are less than 7 digits, you must add leading zeroes to the number.&nbsp; For example, if the case number given to you is 02-7839, it must be entered as '0007839'.</ListItem>
                </List>
                <AccessMsg>
                    <ExclamationIcon /> You can access City of Lubbock Online Police Reports {accessLimit - reportCount} more time(s) today.
                    {errorMessage && <div>{errorMessage}</div>}
                </AccessMsg>
                <FormWrapper>
                    <InputWrapper>
                        <Select name="listYear" id="listYear" ref={yearRef}>
                            {years.map((year, index) => (
                                <option key={index} value={year}>{year}</option>
                            ))}
                        </Select>
                        <Input
                            type="text"
                            placeholder="0000000"
                            pattern="\d{7}"
                            title="Please enter a 7 digit number"
                            maxLength={7}
                            onKeyDown={handleKeyDown}
                            ref={caseNumberRef}
                        />
                        <Input type="date" ref={dateRef} />
                    </InputWrapper>
                    <Button type="button" name="btnSubmit" value="Submit" disabled={!isMaxReports} onClick={handleSubmit}>Submit</Button>
                </FormWrapper>
                <Text>
                    The Lubbock Police Department is committed to collecting and disseminating police report information in a timely and accurate manner. Reports are classified based upon information supplied to the Lubbock Police Department by reporting parties. The report classifications may be changed following subsequent investigation. The Lubbock Police Department does not guarantee (either expressed or implied) the accuracy or completeness of the information contained in these reports.
                </Text>
                <Text>
                    The Lubbock Police Department is not responsible for any error or omission, or for the use of, or the results obtained from, the use of this information. Due to the preliminary nature of the reports, media outlets should make a diligent effort to confirm any information prior to reporting it. Confirmation can be made by contacting the Public Information Unit, the appropriate investigative division, or other departmental authorities.
                </Text>
                <Text>
                    The Lubbock Police Department believes that providing open and simple access to police reports online reinforces our commitment to transparency. However, in disseminating this information we must also comply with current laws that regulate the release of potentially sensitive and confidential information. To ensure that private information is protected and the standards delineated by the Texas Public Information Act are met, certain information is automatically redacted and some reports are not available on the site. The exclusions include, but are not limited to:
                </Text>
                <List>
                    <ListItem>Sexually oriented offenses</ListItem>
                    <ListItem>Offenses where juveniles (individuals under 17 years of age) are the complainant</ListItem>
                    <ListItem>Reference to individuals considered suspects and witnesses</ListItem>
                    <ListItem>Detailed descriptions of property items considered evidence</ListItem>
                    <ListItem>Crash reports, which may be obtained here: <a href="https://policereports.lexisnexis.com">policereports.lexisnexis.com</a></ListItem>
                </List>
                <Text>
                    Individuals who believe that additional or more complete information disclosure is warranted are encouraged to submit an open records request, per state law.
                </Text>
                <Text>
                    Open records requests must be made in writing. For full information and instructions see: <a href="https://www.mylubbock.us/pages/public-information-act">www.mylubbock.us/pages/public-information-act</a>
                </Text>
                <Text>
                    The user specifically acknowledges that the Lubbock Police Department is not responsible for any defamatory, offensive, misleading, or illegal conduct of other users, links, or third parties and that the risk of injury from the foregoing rests entirely with the user. The unauthorized use of the words, <SemiBold>Lubbock Police Department</SemiBold> or the Lubbock Police Department logo or any colorable imitation of either is unlawful. This web page does not, in any way, authorize such use.
                </Text>
            </Container>
        </ThemeProvider>
    );
};

export default ReportSearch;
